import produce, { Draft } from 'immer';
import storage from 'localforage';
import reduceReducers from 'reduce-reducers';
import { persistReducer } from 'redux-persist';

import {
    PHRASES_SELECTOR_GET_KEYWORDS,
    PHRASES_SELECTOR_GET_STATS,
    PHRASES_SELECTOR_HIDE_EDITOR,
    PHRASES_SELECTOR_OPEN_EDITOR,
    PHRASES_SELECTOR_RESET_STATUS,
    PHRASES_SELECTOR_SAVE_PHRASES,
    PHRASES_SELECTOR_SAVE_PHRASES_ERROR,
    PHRASES_SELECTOR_SET_GROUP_INFO,
    PHRASES_SELECTOR_SET_GROUP_KEYWORDS,
    PHRASES_SELECTOR_SET_LOADING_FALSE,
    PHRASES_SELECTOR_SET_START_PHRASES,
    PHRASES_SELECTOR_SET_START_PHRASES_ERROR,
    PHRASES_SELECTOR_SHOW_EDITOR,
    PHRASES_SELECTOR_UPDATE_START_PHRASES,
    PhrasesSelectorAction
} from '../../actions/phrases-selector';
import { PHRASES_SELECTOR_ADD_KEYWORDS } from '../../actions/phrases-selector/persistentData';
import {
    expandStartPhrases,
    getGroupKeywordsMap,
    getPhraseNodeDefaultState
} from '../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { VirtualTreeHelpers } from '../../service/VirtualTreeHelpers/VirtualTreeHelpers';
import { GroupType } from '../../typings/domain';
import { PhrasesSelectorState } from '../../typings/phrases';
import { PhrasesSelectorsState } from '../../typings/state';

import { PhraseSelectorTransform, phrasesSelectorsPersistentReducer } from './persistentData';
import { getDefaultRefinementsState, phrasesSelectorRefinementsReducer } from './refinements';

const phrasesSelectorInitialState: PhrasesSelectorState = {
    groupInfo: {
        id: '',
        minusKeywords: [],
        keywords: {},
        regionIds: [],
        name: '',
        groupType: GroupType.TEXT,
        campaign: {
            name: '',
            id: '',
            strategy: {
                isAutoBudget: false
            }
        }
    },

    isOpen: false,
    isLoading: false,
    startPhrases: {
        similar: [],
        nested: []
    },
    refinements: getDefaultRefinementsState(),
    startPhrasesError: ''
};

export const getPhrasesSelectorInitialState = (): PhrasesSelectorState => phrasesSelectorInitialState;

const phrasesSelectorReducer = reduceReducers(
    phrasesSelectorRefinementsReducer,
    (state: PhrasesSelectorState, action: PhrasesSelectorAction): PhrasesSelectorState => {
        return produce(state, (draftState: Draft<PhrasesSelectorState>) => {
            switch (action.type) {
                case PHRASES_SELECTOR_GET_KEYWORDS:
                case PHRASES_SELECTOR_GET_STATS:
                case PHRASES_SELECTOR_SAVE_PHRASES:
                    draftState.isLoading = true;
                    return;

                case PHRASES_SELECTOR_SET_START_PHRASES:
                    draftState.startPhrases.nested = expandStartPhrases(
                        draftState.startPhrases.nested,
                        action.phrases.nested
                    );
                    return;

                case PHRASES_SELECTOR_UPDATE_START_PHRASES:
                    draftState.startPhrases.nested = action.phrases;
                    return;

                case PHRASES_SELECTOR_ADD_KEYWORDS:
                    draftState.startPhrases.nested = VirtualTreeHelpers.updateNodes(
                        state.startPhrases.nested,
                        action.phrases.map(p => p.name),
                        { state: getPhraseNodeDefaultState(), children: [] }
                    );
                    return;
                case PHRASES_SELECTOR_OPEN_EDITOR:
                case PHRASES_SELECTOR_SET_GROUP_INFO:
                    draftState.groupInfo = action.groupInfo;
                    return;
                case PHRASES_SELECTOR_SHOW_EDITOR:
                    draftState.isOpen = true;
                    return;
                case PHRASES_SELECTOR_HIDE_EDITOR:
                    return getPhrasesSelectorInitialState();
                case PHRASES_SELECTOR_SAVE_PHRASES_ERROR:
                case PHRASES_SELECTOR_SET_LOADING_FALSE:
                    draftState.isLoading = false;
                    return;
                case PHRASES_SELECTOR_SET_START_PHRASES_ERROR:
                    draftState.startPhrasesError = action.message;
                    draftState.isLoading = false;
                    return;
                case PHRASES_SELECTOR_RESET_STATUS:
                    draftState.isLoading = false;
                    draftState.startPhrasesError = '';
                    return;
                case PHRASES_SELECTOR_SET_GROUP_KEYWORDS:
                    draftState.groupInfo.keywords = getGroupKeywordsMap(action.keywords);
                    return;
                default:
                    return;
            }
        });
    }
);

export const phrasesSelectorsReducer = persistReducer(
    {
        key: 'phrases-selector-persistent-data',
        storage,
        whitelist: ['persistentData'],
        transforms: [PhraseSelectorTransform]
    },
    (
        state: PhrasesSelectorsState = {
            persistentData: {}
        },
        action: PhrasesSelectorAction
    ): PhrasesSelectorsState => {
        return produce(phrasesSelectorsPersistentReducer(state, action), (draftState: Draft<PhrasesSelectorsState>) => {
            const wizardId = action.wizardId;
            if (wizardId) {
                const wizardState = state[wizardId] || getPhrasesSelectorInitialState();

                draftState[wizardId] = phrasesSelectorReducer(wizardState, action);
            }
        });
    }
);
